import { useEffect, useMemo, useRef, useState } from 'react';
import { Download, Pause, Play } from 'lucide-react';
import Pill from '../components/Pill.jsx';
import { api } from '../lib/api.js';
import { formatDuration, formatNumber, formatWhen } from '../lib/format.js';
import { useSession, ROLES } from '../lib/session.jsx';

const PER_PAGE = 25;

const FOLDER_LABELS = { INBOX: 'New', Urgent: 'Urgent', Old: 'Saved' };
const FOLDER_TONES = { INBOX: 'ringing', Urgent: 'missed', Old: 'offline' };

/* Voicemail. Admin sees every mailbox that has messages; an extension user
   sees their own. The audio is streamed through the portal (see the /voicemail
   proxy) — the browser never touches the PBX directly. */
export default function Voicemail() {
  const { role } = useSession();
  const [state, setState] = useState({ status: 'loading', items: [], error: null });
  const [page, setPage] = useState(1);
  const [playing, setPlaying] = useState(null); // message id currently playing

  useEffect(() => {
    const ac = new AbortController();
    api
      .get('/voicemail', { signal: ac.signal })
      .then(({ items }) => setState({ status: 'ready', items, error: null }))
      .catch((err) => {
        if (err.name === 'AbortError') return;
        setState({ status: 'error', items: [], error: err });
      });
    return () => ac.abort();
  }, []);

  const { items } = state;
  const unheard = useMemo(() => items.filter((m) => m.unheard).length, [items]);
  const pages = Math.max(1, Math.ceil(items.length / PER_PAGE));
  const shown = items.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const showMailbox = role !== ROLES.EXTENSION_USER;

  if (state.status === 'loading') {
    return <div className="card"><div className="empty"><p className="empty__body">Loading your voicemail…</p></div></div>;
  }
  if (state.status === 'error') {
    return (
      <div className="card"><div className="empty">
        <div className="empty__title">We couldn't load your voicemail</div>
        <p className="empty__body">{state.error?.message}</p>
      </div></div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="card"><div className="empty">
        <div className="empty__title">No voicemail</div>
        <p className="empty__body">There are no voicemail messages on your account.</p>
      </div></div>
    );
  }

  return (
    <div>
      <p className="footnote" style={{ marginTop: 0, marginBottom: 12 }}>
        {items.length} message{items.length === 1 ? '' : 's'}
        {unheard > 0 && <> · <b style={{ color: 'var(--signal-deep)' }}>{unheard} new</b></>}
      </p>

      <div className="card">
        {shown.map((m, i) => (
          <VoicemailRow
            key={m.id}
            message={m}
            showMailbox={showMailbox}
            isPlaying={playing === m.id}
            onPlay={() => setPlaying(playing === m.id ? null : m.id)}
            last={i === shown.length - 1}
          />
        ))}
      </div>

      {pages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14 }}>
          <button type="button" className="btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--ink-soft)' }}>
            Page {page} of {pages} · {items.length} message{items.length === 1 ? '' : 's'}
          </span>
          <button type="button" className="btn" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      )}
    </div>
  );
}

function VoicemailRow({ message, showMailbox, isPlaying, onPlay, last }) {
  const audioRef = useRef(null);

  // Drive the real <audio> element from the row's play/pause button.
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    if (isPlaying) el.play().catch(() => {});
    else el.pause();
  }, [isPlaying]);

  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px',
        borderBottom: last ? 'none' : '1px solid var(--line)',
        background: message.unheard ? 'var(--signal-wash)' : 'transparent',
      }}
    >
      <button
        type="button"
        onClick={onPlay}
        aria-label={isPlaying ? 'Pause' : 'Play'}
        style={{
          width: 38, height: 38, borderRadius: '50%', border: 'none', flex: 'none',
          background: 'var(--red-grad)', color: '#fff', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {isPlaying ? <Pause size={16} fill="#fff" /> : <Play size={16} fill="#fff" />}
      </button>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
          <b style={{ fontWeight: 600, fontSize: 14 }}>{formatNumber(message.from)}</b>
          {message.unheard && <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--signal)' }} />}
          <Pill tone={FOLDER_TONES[message.folder] ?? 'offline'}>{FOLDER_LABELS[message.folder] ?? message.folder}</Pill>
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--ink-soft)' }}>
          {showMailbox && <>{message.extension_name ?? `Ext ${message.extension}`} · </>}
          {formatWhen(message.received_at)}
        </div>
        {isPlaying && (
          <audio ref={audioRef} src={message.audio_url} controls autoPlay style={{ width: '100%', marginTop: 8, height: 34 }} />
        )}
      </div>

      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--ink-soft)', width: 46, textAlign: 'right' }}>
        {formatDuration(message.duration)}
      </span>

      <a
        href={message.download_url}
        aria-label="Download"
        title="Download"
        style={{ color: 'var(--ink-soft)', display: 'flex' }}
      >
        <Download size={16} />
      </a>
    </div>
  );
}
